import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle, MapPin, Clock, ArrowRight, Briefcase, Award, Target, Upload, MessageCircle, X, FileText } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { getJobs } from "../lib/jobStore";
import { useForm, ValidationError } from "@formspree/react";

const CAREERS_IMG = "/assets/atf_hero_bg.png";

const FadeUp = ({ children, delay = 0, className = "" }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8, delay: delay / 1000, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const benefits = [
  "Industry-Leading Compensation",
  "Full PF, ESI & Medical Coverage",
  "Professional Skill Training via RSRMA equivalent",
  "Merit-Based Corporate Promotions",
  "Respect and Institutional Pride",
  "Pan-India Career Advancement",
];

// Roles are fetched from Firebase Firestore — globally visible to all users

const criteria = [
  "Full Time Graduation with minimum 50% aggregate (or 10+2 for Guard roles)",
  "Minimum Height: Male 167.6 cm / Female 157.5 cm (relaxation for hilly regions)",
  "NCC (National Cadet Corps) candidates preferred but not mandatory",
  "Absolute integrity, clear record, and physical fitness to pass the PET (Physical Endurance Test)",
];

const applicationSteps = [
  { step: "01", title: "Online Registration", desc: "Submit your academic credentials and basic health profile via our secure career portal." },
  { step: "02", title: "Physical Endurance Test", desc: "Baseline evaluation including 800m run, sit-ups, and push-ups at regional selection centers." },
  { step: "03", title: "Document & Background Audit", desc: "Complete police and local reference verification (Aadhaar, PAN, Marksheets, NCC)." },
  { step: "04", title: "Professional Induction", desc: "12-month rigorous training period at our Security Management Academy before final posting." },
];

export default function Careers() {
  const [openRoles, setOpenRoles] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [expandedId, setExpandedId] = React.useState(null);
  const [state, handleSubmit] = useForm("mdayangv"); 
  const [formData, setFormData] = React.useState({
    full_name: "",
    email: "",
    phone: "",
    description: "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleWhatsApp = (data) => {
    const message = `Hi ATF Team, I am interested in applying for a position.\n\nName: ${data.full_name}\nPhone: ${data.phone}\nEmail: ${data.email}\nDescription: ${data.description}\n\n(CV was attached in the web form)`;
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/917567137300?text=${encodedMessage}`, "_blank");
  };

  const handleCustomSubmit = async (e) => {
    e.preventDefault();
    // Call the original formspree submit if needed, or just redirect to WhatsApp
    // To ensure the user's data is also captured by Formspree:
    await handleSubmit(e);
    handleWhatsApp(formData);
  };

  React.useEffect(() => {
    const fetchJobs = async () => {
      try {
        const data = await getJobs();
        setOpenRoles(data);
      } catch (err) {
        console.error("Failed to fetch jobs:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, []);

  return (
    <main className="bg-[#F9F8F5] font-sans text-[#141414]" data-testid="careers-page">
      {/* ── 01. Hero Component ── */}
      <section className="bg-[#F9F8F5] h-[85vh] md:h-[75vh] flex items-center relative overflow-hidden" data-testid="careers-hero">
        <div className="absolute inset-0 z-0">
          {/* Split-Screen Slanted Overlay */}
          <div 
            className="absolute inset-y-0 left-0 w-full lg:w-[40%] bg-[#F9F8F5]/90 backdrop-blur-sm z-10 pointer-events-none" 
            style={{ clipPath: "polygon(0 0, 85% 0, 100% 100%, 0 100%)" }}
          />
          
          <div className="absolute inset-0 z-0">
            <img src="/career.png" alt="Corporate Recruitment" className="w-full h-full object-cover object-[85%_center]" />
          </div>
          <div className="absolute inset-0 bg-[#835F21]/5 mix-blend-multiply z-10" />
        </div>
        
        <div className="relative z-20 w-full lg:w-[40%] px-6 md:px-12 lg:pl-24 lg:pr-12 py-12 md:py-16">
          <div className="relative z-10">
            <FadeUp>
              <span className="section-label block mb-6 text-[#835F21]">Professional Recruitment</span>
              <h1 className="font-display font-medium text-[#141414] leading-[1.05] mb-8 tracking-tight"
                style={{ fontSize: "clamp(20px, 3vw, 44px)" }}
              >
                Turn Your Passion <br />
                <span className="text-[#835F21] italic font-light mt-2 block">Into Profession.</span>
              </h1>
              <p className="font-sans text-lg md:text-xl text-[#141414]/70 leading-relaxed font-light">
                ATF is seeking the best and brightest young men and women for an exciting career as professional security officers protecting India's economic wealth.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ── 02. The ATF Mandate ── */}
      <section className="bg-white py-32 md:py-48" data-testid="careers-benefits">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-20 lg:gap-32 items-center">
          <FadeUp>
            <span className="font-sans text-[11px] font-bold tracking-[0.2em] text-[#835F21] uppercase block mb-6">The ATF Standard</span>
            <h2 className="font-display font-medium text-[#141414] leading-[1.05] mb-10 tracking-tight" style={{ fontSize: "clamp(40px, 4vw, 56px)" }}>
              A Framework for <br /> <span className="italic font-light">Professional Growth.</span>
            </h2>
            <p className="font-sans text-[16px] text-[#141414]/70 leading-relaxed mb-12 font-light">
              We provide best-in-class training opportunities through our dedicated security academy. You will be trained on the latest industry trends, compliance practices, and granted an unyielding structure for advancement.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {benefits.map((b, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#835F21]/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle size={14} className="text-[#835F21]" />
                  </div>
                  <span className="font-sans text-[13px] font-medium text-[#141414] uppercase tracking-wide leading-relaxed pt-1.5">{b}</span>
                </div>
              ))}
            </div>
          </FadeUp>
          
          <FadeUp delay={200} className="relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-[#141414]/5 group">
              <img src={CAREERS_IMG} alt="ATF Force Alignment" className="w-full aspect-[4/5] lg:aspect-square object-cover opacity-80 group-hover:scale-105 transition-transform duration-1000" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="absolute bottom-10 left-10 right-10">
                <div className="bg-[#141414]/90 backdrop-blur-md p-10 rounded-2xl text-white shadow-2xl border border-white/10 group-hover:bg-[#141414] transition-colors duration-500">
                  <h3 className="font-display font-medium text-2xl mb-4 leading-tight tracking-tight">
                    "Growth is Life. Safeguarding the nation's progress."
                  </h3>
                  <p className="font-sans text-[10px] font-bold tracking-[0.2em] uppercase text-[#835F21]">— ATF Recruitment</p>
                </div>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ── 03. Active Deployments ── */}
      <section className="bg-[#F0EBE6] py-32 md:py-48" data-testid="careers-open-roles">
        <div className="max-w-7xl mx-auto px-6">
          <FadeUp className="mb-20 text-center md:text-left flex flex-col md:flex-row justify-between items-end gap-8">
            <div>
              <span className="font-sans text-[11px] font-bold tracking-[0.2em] text-[#835F21] uppercase block mb-6">Open Positions</span>
              <h2 className="font-display font-medium text-[#141414] tracking-tight" style={{ fontSize: "clamp(44px, 5vw, 64px)" }}>
                Active <span className="italic font-light">Opportunities</span>
              </h2>
            </div>
            <a href="#apply-form" className="btn-primary h-14 bg-[#141414] text-white hover:bg-[#835F21] px-10">
              Submit General Application
            </a>
          </FadeUp>
          
          <div className="grid grid-cols-1 gap-6">
            {loading ? (
              <div className="py-20 text-center font-sans text-[#141414]/20 uppercase tracking-[0.3em] font-bold">Scanning Active Roles...</div>
            ) : openRoles.length === 0 ? (
              <div className="py-20 text-center bg-white border border-dashed border-[#141414]/10 rounded-2xl">
                <Briefcase size={40} className="mx-auto text-[#141414]/10 mb-4" />
                <p className="font-sans text-[#141414]/40 text-sm uppercase font-bold tracking-widest">No active deployments currently listed</p>
              </div>
            ) : (
              openRoles.map((r, i) => (
                <FadeUp key={r.id || i} delay={i * 100}>
                  <div className="bg-white rounded-2xl p-8 lg:p-10 flex flex-col gap-6 hover:shadow-[0_20px_60px_rgba(0,0,0,0.06)] transition-all duration-500 border border-[#141414]/5 hover:-translate-y-1 group">
                    {/* Top row: icon + title/meta + apply button */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                      <div className="flex items-center gap-6 lg:gap-8">
                        <div className="w-16 h-16 rounded-2xl bg-[#F9F8F5] flex items-center justify-center group-hover:bg-[#835F21] group-hover:scale-110 transition-all duration-500 flex-shrink-0">
                          <Briefcase size={24} className="text-[#835F21] group-hover:text-white transition-colors" />
                        </div>
                        <div>
                          <h3 className="font-display font-medium text-2xl text-[#141414] mb-3 group-hover:text-[#835F21] transition-colors">{r.role}</h3>
                          <div className="flex flex-wrap items-center gap-6">
                            <span className="font-sans text-[11px] font-bold text-[#141414]/50 flex items-center gap-2 uppercase tracking-[0.15em]">
                              <MapPin size={14} className="text-[#835F21]" /> {r.location}
                            </span>
                            <span className="font-sans text-[11px] font-bold text-[#141414]/50 flex items-center gap-2 uppercase tracking-[0.15em]">
                              <Clock size={14} className="text-[#835F21]" /> {r.type}
                            </span>
                          </div>
                        </div>
                      </div>
                      <button 
                        onClick={() => setExpandedId(expandedId === (r.id || i) ? null : (r.id || i))}
                        className="inline-flex items-center gap-3 text-[#141414] font-sans text-[11px] font-bold tracking-widest uppercase group/link hover:text-[#835F21] transition-colors md:mr-4 flex-shrink-0"
                      >
                        {expandedId === (r.id || i) ? "Close Details" : "View Details"}
                        <div className={`w-10 h-10 rounded-full border border-[#141414]/20 flex items-center justify-center transition-all ${
                          expandedId === (r.id || i) ? "bg-[#141414] text-white border-[#141414]" : "group-hover/link:border-[#835F21] group-hover/link:bg-[#835F21] group-hover/link:text-white"
                        }`}>
                          <ArrowRight size={16} className={`transition-transform duration-500 ${expandedId === (r.id || i) ? "-rotate-90" : ""}`} />
                        </div>
                      </button>
                    </div>

                    {/* Accordion Content */}
                    <AnimatePresence>
                      {expandedId === (r.id || i) && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                          className="overflow-hidden"
                        >
                          <div className="pt-8 mt-8 border-t border-[#141414]/5">
                            <div className="flex items-center gap-4 mb-6">
                              <FileText size={16} className="text-[#835F21]" />
                              <span className="font-sans text-[10px] font-bold tracking-[0.2em] text-[#835F21] uppercase">Operational Requirements</span>
                            </div>
                            <p className="font-sans text-[15px] text-[#141414]/60 leading-relaxed font-light whitespace-pre-wrap mb-10">
                              {r.description || "No additional description provided for this role."}
                            </p>
                            <button 
                              onClick={() => document.getElementById("apply-form")?.scrollIntoView({ behavior: "smooth" })}
                              className="btn-primary h-12 px-8 bg-[#141414] text-white hover:bg-[#835F21] text-[10px]"
                            >
                              Apply for this role
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </FadeUp>
              ))
            )}
          </div>
        </div>
      </section>

      {/* ── Career Application Form ── */}
      <section className="bg-white py-32 md:py-48" id="apply-form" data-testid="careers-form">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-20">
          <div className="lg:col-span-5">
            <FadeUp>
              <span className="font-sans text-[11px] font-bold tracking-[0.2em] text-[#835F21] uppercase block mb-6">Direct Application</span>
              <h2 className="font-display font-medium text-[#141414] leading-[1.05] mb-8 tracking-tight" style={{ fontSize: "clamp(40px, 4.5vw, 56px)" }}>
                Submit Your <br /> <span className="italic font-light">Credentials.</span>
              </h2>
              <p className="font-sans text-lg text-[#141414]/60 leading-relaxed font-light mb-10">
                Provide your details and upload your latest CV. Our recruitment team will review your profile and contact you for the next phase.
              </p>
              
           
            </FadeUp>
          </div>

          <div className="lg:col-span-7">
            <FadeUp delay={200}>
              <div className="bg-white rounded-[2.5rem] p-8 md:p-12 shadow-[0_30px_60px_rgba(0,0,0,0.02)] border border-[#141414]/5">
                {state.succeeded ? (
                  <div className="text-center py-10">
                    <CheckCircle size={48} className="text-[#835F21] mx-auto mb-6" />
                    <h3 className="font-display font-medium text-2xl text-[#141414] mb-4">Application Submitted</h3>
                    <p className="font-sans text-[#141414]/60 mb-8">Our recruitment officers have received your credentials. Please keep your phone active for communication.</p>
                    <button onClick={() => window.location.reload()} className="btn-primary h-12 px-8">Apply for another role</button>
                  </div>
                ) : (
                  <form onSubmit={handleCustomSubmit} className="space-y-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div>
                        <label className="font-sans text-[10px] font-bold tracking-[0.2em] text-[#141414]/40 uppercase block mb-3">Full Name *</label>
                        <input 
                          type="text" 
                          name="full_name" 
                          required 
                          value={formData.full_name}
                          onChange={handleChange}
                          className="w-full bg-[#F9F8F5] border border-[#141414]/5 rounded-xl px-6 h-14 focus:outline-none focus:ring-2 focus:ring-[#835F21] transition-all"
                          placeholder="Mehul Barot"
                        />
                      </div>
                      <div>
                        <label className="font-sans text-[10px] font-bold tracking-[0.2em] text-[#141414]/40 uppercase block mb-3">Phone Number *</label>
                        <input 
                          type="tel" 
                          name="phone" 
                          required 
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full bg-[#F9F8F5] border border-[#141414]/5 rounded-xl px-6 h-14 focus:outline-none focus:ring-2 focus:ring-[#835F21] transition-all"
                          placeholder="+91 9XXXX XXXXX"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="font-sans text-[10px] font-bold tracking-[0.2em] text-[#141414]/40 uppercase block mb-3">Email Address *</label>
                      <input 
                        type="email" 
                        name="email" 
                        required 
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full bg-[#F9F8F5] border border-[#141414]/5 rounded-xl px-6 h-14 focus:outline-none focus:ring-2 focus:ring-[#835F21] transition-all"
                        placeholder="corporate@atfsecure.com"
                      />
                    </div>

                    <div>
                      <label className="font-sans text-[10px] font-bold tracking-[0.2em] text-[#141414]/40 uppercase block mb-3">Brief Description / Experience</label>
                      <textarea 
                        name="description" 
                        rows="4"
                        value={formData.description}
                        onChange={handleChange}
                        className="w-full bg-[#F9F8F5] border border-[#141414]/5 rounded-xl px-6 py-4 focus:outline-none focus:ring-2 focus:ring-[#835F21] transition-all resize-none"
                        placeholder="Tell us about your background..."
                      />
                    </div>

                    <div>
                      <label className="font-sans text-[10px] font-bold tracking-[0.2em] text-[#141414]/40 uppercase block mb-3">Upload CV (PDF/DOCX) *</label>
                      <div className="relative group">
                        <input 
                          type="file" 
                          name="cv" 
                          required 
                          accept=".pdf,.doc,.docx"
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                        />
                        <div className="w-full bg-[#F9F8F5] border-2 border-dashed border-[#141414]/10 rounded-xl px-6 h-24 flex items-center justify-center gap-4 transition-colors group-hover:border-[#835F21] group-hover:bg-[#835F21]/5">
                          <Upload size={20} className="text-[#835F21]" />
                          <span className="font-sans text-[13px] font-medium text-[#141414]/40">Click to upload your file</span>
                        </div>
                      </div>
                    </div>

                    <button 
                      type="submit" 
                      disabled={state.submitting}
                      className="btn-primary w-full h-15 bg-[#141414] text-white hover:bg-[#835F21] shadow-2xl tracking-[0.2em]"
                    >
                      {state.submitting ? "UPLOADING..." : "SUBMIT APPLICATION"}
                      <ArrowRight size={18} className="ml-3" />
                    </button>
                  </form>
                )}
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ── 04. Protocol & Flow ── */}
      <section className="bg-[#141414] py-32 md:py-48" data-testid="careers-criteria">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-24 lg:gap-32">
            <FadeUp>
              <Target size={40} className="text-[#835F21] mb-10 stroke-[1px]" />
              <span className="font-sans text-[11px] font-bold tracking-[0.2em] text-[#835F21] uppercase block mb-6">Eligibility & Criteria</span>
              <h2 className="font-display font-medium text-white leading-[1.05] mb-12 tracking-tight" style={{ fontSize: "clamp(40px, 4.5vw, 60px)" }}>
                Are You Ready <br /> <span className="italic font-light">To Excel?</span>
              </h2>
              <div className="space-y-6">
                {criteria.map((c, i) => (
                  <div key={i} className="flex items-center gap-6 border border-white/10 bg-white/5 rounded-2xl p-6 hover:bg-[#835F21]/10 hover:border-[#835F21]/50 transition-colors group">
                    <Award size={24} className="text-[#835F21] flex-shrink-0" strokeWidth={1.5} />
                    <span className="font-sans text-[13px] font-medium text-white uppercase tracking-wide leading-relaxed group-hover:text-[#835F21] transition-colors">{c}</span>
                  </div>
                ))}
              </div>
            </FadeUp>

            <FadeUp delay={200}>
              <div className="lg:pl-16">
                <span className="font-sans text-[11px] font-bold tracking-[0.2em] text-[#835F21] uppercase block mb-6">The Journey</span>
                <h2 className="font-display font-medium text-white leading-[1.05] mb-16 tracking-tight" style={{ fontSize: "clamp(40px, 4.5vw, 60px)" }}>
                  The Integration <br /> <span className="italic font-light">Lifecycle</span>
                </h2>
                <div className="space-y-12 relative">
                  {/* Vertical Track */}
                  <div className="absolute left-7 top-4 bottom-4 w-[1px] bg-gradient-to-b from-[#835F21] via-white/10 to-transparent" />
                  
                  {applicationSteps.map((s, i) => (
                    <div key={i} className="flex items-start gap-8 relative z-10 group">
                      <div className="w-14 h-14 rounded-full bg-[#141414] border-2 border-[#835F21] text-white font-sans font-bold text-sm flex items-center justify-center flex-shrink-0 shadow-[0_0_20px_rgba(131,95,33,0.2)] group-hover:bg-[#835F21] transition-colors duration-500 mt-[-4px]">
                        {s.step}
                      </div>
                      <div>
                        <h4 className="font-display font-medium text-2xl text-white mb-3 tracking-tight group-hover:text-[#835F21] transition-colors">{s.title}</h4>
                        <p className="font-sans text-[15px] text-white/50 leading-relaxed font-light">{s.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ── 05. Final Call ── */}
      <section className="bg-[#835F21] py-32" data-testid="careers-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <FadeUp>
            <h2 className="font-display font-medium text-white mb-10 tracking-tight" style={{ fontSize: "clamp(36px, 5vw, 64px)" }}>
              Ready to Advance <span className="italic font-light">Your Career?</span>
            </h2>
            <a href="#apply-form" className="btn-primary bg-[#141414] text-white border-none shadow-[0_0_40px_rgba(0,0,0,0.3)] hover:bg-[#F9F8F5] hover:text-[#141414] h-16 px-12 text-sm flex items-center justify-center mx-auto w-fit group tracking-[0.2em]">
              Register Now 
              <ArrowRight size={18} className="ml-3 group-hover:translate-x-2 transition-transform" />
            </a>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
